import { type ConnectionStatus, Network } from '@capacitor/network'
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

/**
 * Network connectivity state.
 *
 * Kept as a thin abstraction over @capacitor/network so the rest of the app
 * only depends on this hook — makes it trivial to swap the underlying
 * implementation.
 */
export type NetworkState = {
  /** Whether the device currently has network connectivity. */
  isOnline: boolean
  /** Connection type (wifi, cellular, none, unknown). */
  connectionType: ConnectionStatus['connectionType']
}

const initialState: NetworkState = {
  isOnline: navigator.onLine,
  connectionType: 'unknown',
}

const NetworkContext = createContext<NetworkState>(initialState)

export const NetworkProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<NetworkState>(initialState)

  useEffect(() => {
    let mounted = true
    const apply = (
      isOnline: boolean,
      connectionType: ConnectionStatus['connectionType'],
    ) => {
      if (mounted) setState({ isOnline, connectionType })
    }

    // Get initial status
    Network.getStatus().then((status) =>
      apply(status.connected, status.connectionType),
    )

    // Listen for changes
    const handler = Network.addListener(
      'networkStatusChange',
      (status: ConnectionStatus) =>
        apply(status.connected, status.connectionType),
    )

    // Fallback: the webview's own online/offline events. @capacitor/network can
    // miss the reconnect transition (notably on the iOS simulator), which left
    // the offline banner stuck after coming back online.
    const onOnline = () => apply(true, 'unknown')
    const onOffline = () => apply(false, 'none')
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)

    return () => {
      mounted = false
      handler.then((h) => h.remove())
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  return (
    <NetworkContext.Provider value={state}>{children}</NetworkContext.Provider>
  )
}

/**
 * Returns the current network state.
 * Must be used within a `<NetworkProvider>`.
 */
export const useNetwork = (): NetworkState => useContext(NetworkContext)
